import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Save, Loader2, Settings2, Calculator, Shield, CircleDot, Car } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATE = ['36', '48', '60'];
const KM_OPTIONS = ['10000', '15000', '20000', '25000'];
const FRANCHIGIA_COMBOS = ['1000-10', '1000-0', '500-10', '500-0'];
const FRANCHIGIA_LABELS = { '1000-10': '€1.000 / 10%', '1000-0': '€1.000 / 0%', '500-10': '€500 / 10%', '500-0': '€500 / 0%' };
const AUTO_SOSTITUTIVA_CATEGORIE = ['B>8', 'C>8', 'D>8'];
const AUTO_SOSTITUTIVA_TIPI = [
  { key: '4vantage', label: '4VANTAGE' },
  { key: 'nuovo', label: 'NUOVO' },
  { key: 'usato', label: 'USATO' }
];

const ParametriCalcoloPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('nuovo');
  
  const [provvigioni, setProvvigioni] = useState({});
  const [commissionConfig, setCommissionConfig] = useState({});
  const [franchigie, setFranchigie] = useState({ opzioni: [], matrici: {} });
  const [gommeCosti, setGommeCosti] = useState({});
  const [autoSostitutivaCosti, setAutoSostitutivaCosti] = useState({});
  // Fase 2 Task 2.3: aliquota IVA anticipo (usata dal motore cliente Task 2.1)
  const [ivaAnticipoPct, setIvaAnticipoPct] = useState(22);
  // Promo Nuovo Cliente / Rinnovo (importi default modificabili)
  const [promoNuovo, setPromoNuovo] = useState(800);
  const [promoUsato, setPromoUsato] = useState(1000);
  const [promoPdfLabel, setPromoPdfLabel] = useState('Promo Nuovo Cliente / Rinnovo');

  const fetchParams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/parametri-calcolo`, { headers: getAuthHeader() });
      const data = res.data;
      setProvvigioni(data.provvigioni || {});
      setCommissionConfig(data.commission_config || {});
      setFranchigie(data.franchigie || { opzioni: [], matrici: {} });
      setGommeCosti(data.gomme_costi || {});
      setAutoSostitutivaCosti(data.auto_sostitutiva_costi || {});
      if (data.iva_anticipo_pct != null) setIvaAnticipoPct(Number(data.iva_anticipo_pct));
      if (data.promo_nuovo != null) setPromoNuovo(Number(data.promo_nuovo));
      if (data.promo_usato != null) setPromoUsato(Number(data.promo_usato));
      if (data.promo_pdf_label) setPromoPdfLabel(String(data.promo_pdf_label));
    } catch {
      toast.error('Errore caricamento parametri');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchParams(); }, [fetchParams]);

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const payload = {};
      if (section === 'provvigioni' || section === 'all') payload.provvigioni = provvigioni;
      if (section === 'commission_config' || section === 'all') payload.commission_config = commissionConfig;
      if (section === 'franchigie' || section === 'all') payload.franchigie = franchigie;
      if (section === 'gomme' || section === 'all') payload.gomme_costi = gommeCosti;
      if (section === 'auto_sostitutiva' || section === 'all') payload.auto_sostitutiva_costi = autoSostitutivaCosti;
      if (section === 'iva_anticipo' || section === 'all') payload.iva_anticipo_pct = Number(ivaAnticipoPct);
      if (section === 'promo' || section === 'all') {
        payload.promo_nuovo = Number(promoNuovo);
        payload.promo_usato = Number(promoUsato);
        payload.promo_pdf_label = String(promoPdfLabel || '').trim();
      }

      await axios.put(`${API}/admin/parametri-calcolo`, payload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success('Parametri salvati. Le quotazioni useranno i nuovi valori.');
    } catch {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateMultiplier = (tipo, stepIdx, key, value) => {
    setProvvigioni(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (updated[tipo]?.data?.[stepIdx]?.moltiplicatori) {
        updated[tipo].data[stepIdx].moltiplicatori[key] = parseFloat(value) || 1.0;
      }
      return updated;
    });
  };

  const create4VantageTable = () => {
    const maxStep = (commissionConfig['4vantage']?.maxAggiuntivo ?? 6);
    setProvvigioni(prev => {
      const src = JSON.parse(JSON.stringify(prev.nuovo?.data || []));
      return { ...prev, '4vantage': { data: src.filter(r => r.prov_scelta <= maxStep) } };
    });
    toast.info('Tabella 4Vantage creata dalla tabella Nuovo: modifica i valori e premi "Salva Moltiplicatori"');
  };

  const updateFranchigia = (tipo, durata, combo, km, value) => {
    setFranchigie(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.matrici) updated.matrici = {};
      if (!updated.matrici[tipo]) updated.matrici[tipo] = {};
      if (!updated.matrici[tipo][durata]) updated.matrici[tipo][durata] = {};
      if (!updated.matrici[tipo][durata][combo]) updated.matrici[tipo][durata][combo] = {};
      updated.matrici[tipo][durata][combo][km] = parseFloat(value) || 0;
      return updated;
    });
  };

  const updateGomme = (tipo, durata, field, value) => {
    setGommeCosti(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated[tipo]) updated[tipo] = {};
      if (!updated[tipo][durata]) updated[tipo][durata] = {};
      updated[tipo][durata][field] = parseFloat(value) || 0;
      return updated;
    });
  };

  const updateAutoSostitutiva = (categoria, tipo, value) => {
    setAutoSostitutivaCosti(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated[categoria]) updated[categoria] = {};
      updated[categoria][tipo] = parseFloat(value) || 0;
      return updated;
    });
  };

  const isSuperAdmin = user?.role === 'super_admin';
  if (!isSuperAdmin) return <div className="flex items-center justify-center h-64"><p className="text-slate-500">Accesso riservato al Super Admin</p></div>;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const tipoData = provvigioni[activeTab]?.data || [];
  const tipoConfig = commissionConfig[activeTab] || {};
  const franchTipo = activeTab === '4vantage' ? 'nuovo' : activeTab;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>Parametri di Calcolo</h1>
          <p className="text-sm text-slate-500 mt-1">Configura moltiplicatori, franchigie e costi gomme</p>
        </div>
        <Button onClick={() => handleSave('all')} disabled={saving} className="gap-2" data-testid="save-all-params">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva Tutto
        </Button>
      </div>

      {/* Tab Tipo Veicolo */}
      <div className="flex gap-2">
        {[
          { key: 'nuovo', label: 'NUOVO', color: 'bg-blue-600' },
          { key: 'usato', label: 'USATO', color: 'bg-emerald-600' },
          { key: '4vantage', label: '4VANTAGE', color: 'bg-violet-600' },
        ].map(tab => (
          <Button key={tab.key} variant={activeTab === tab.key ? 'default' : 'outline'}
            className={activeTab === tab.key ? `${tab.color} text-white` : ''}
            onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}>
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Commission Config */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings2 className="w-4 h-4" /> Configurazione Provvigioni - {activeTab.toUpperCase()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <Label className="text-xs">Base %</Label>
              <Input type="number" step="1" min="0" max="20" value={tipoConfig.base || 0}
                onChange={e => setCommissionConfig(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], base: parseInt(e.target.value) || 0 } }))} />
            </div>
            <div>
              <Label className="text-xs">Max Aggiuntivo %</Label>
              <Input type="number" step="1" min="0" max="20" value={tipoConfig.maxAggiuntivo || 0}
                onChange={e => setCommissionConfig(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], maxAggiuntivo: parseInt(e.target.value) || 0 } }))} />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Totale: {(tipoConfig.base || 0)}% base + max {(tipoConfig.maxAggiuntivo || 0)}% = {(tipoConfig.base || 0) + (tipoConfig.maxAggiuntivo || 0)}% ({tipoData.length} step)
          </p>
        </CardContent>
      </Card>

      {/* Tabella Moltiplicatori */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="w-4 h-4" /> Moltiplicatori Provvigionali - {activeTab.toUpperCase()}
          </CardTitle>
          <CardDescription>
            Ogni riga = 1 step aggiuntivo. I valori moltiplicano il canone base.
            {activeTab === '4vantage' && ' Per il 4Vantage (48 mesi / 30.000 km) si applica la colonna 48m/25k.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {activeTab === '4vantage' && tipoData.length === 0 && (
              <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-800 flex flex-wrap items-center justify-between gap-3" data-testid="provvigioni-4v-empty">
                <span>Tabella 4Vantage non ancora definita: i canoni 4Vantage usano la tabella NUOVO.</span>
                <Button size="sm" variant="outline" onClick={create4VantageTable} data-testid="provvigioni-4v-create">
                  Crea tabella 4Vantage (copia da Nuovo)
                </Button>
              </div>
            )}
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border px-2 py-1.5 text-left font-medium text-slate-700 w-16">Step</th>
                  <th className="border px-2 py-1.5 text-left font-medium text-slate-700 w-14">%</th>
                  {DURATE.map(d => KM_OPTIONS.map(km => (
                    <th key={`${d}_${km}`} className="border px-1 py-1.5 text-center font-medium text-slate-700 text-[10px]">
                      {d}m/{(parseInt(km)/1000)}k
                    </th>
                  ))).flat()}
                </tr>
              </thead>
              <tbody>
                {tipoData.map((step, idx) => (
                  <tr key={`step-${activeTab}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="border px-2 py-1"><Badge variant="outline" className="text-xs">{idx}</Badge></td>
                    <td className="border px-2 py-1 text-xs text-slate-600">{(tipoConfig.base || 0) + idx}%</td>
                    {DURATE.map(d => KM_OPTIONS.map(km => {
                      const key = `${d}_${km}`;
                      return (
                        <td key={key} className="border px-0.5 py-0.5">
                          <Input type="number" step="0.01" min="1.00" max="2.00"
                            value={step.moltiplicatori?.[key] || 1.0}
                            onChange={e => updateMultiplier(activeTab, idx, key, e.target.value)}
                            className="h-7 text-xs text-center px-1 w-16" />
                        </td>
                      );
                    })).flat()}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={() => handleSave('provvigioni')} disabled={saving} className="gap-1">
              <Save className="w-3 h-3" /> Salva Moltiplicatori
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Franchigie - Matrice completa */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="w-4 h-4" /> Matrice Franchigie - {franchTipo.toUpperCase()}
          </CardTitle>
          <CardDescription>Percentuale di incidenza sul canone base per ogni combinazione franchigia/durata/km</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {DURATE.map(durata => (
            <div key={durata}>
              <h4 className="font-medium text-sm text-slate-700 mb-1">{durata} Mesi</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse max-w-2xl">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border px-2 py-1 text-left font-medium text-slate-700">Franchigia</th>
                      {KM_OPTIONS.map(km => (
                        <th key={km} className="border px-2 py-1 text-center font-medium text-slate-700 text-xs">{(parseInt(km)/1000)}k km</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {FRANCHIGIA_COMBOS.map(combo => (
                      <tr key={combo}>
                        <td className="border px-2 py-1 font-medium text-xs">{FRANCHIGIA_LABELS[combo]}</td>
                        {KM_OPTIONS.map(km => (
                          <td key={km} className="border px-0.5 py-0.5">
                            <Input type="number" step="0.001" min="0"
                              value={franchigie.matrici?.[franchTipo]?.[durata]?.[combo]?.[km] ?? 0}
                              onChange={e => updateFranchigia(franchTipo, durata, combo, km, e.target.value)}
                              className="h-7 text-xs text-center px-1 w-20" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <Button size="sm" onClick={() => handleSave('franchigie')} disabled={saving} className="gap-1">
              <Save className="w-3 h-3" /> Salva Franchigie
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gomme */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CircleDot className="w-4 h-4" /> Costi Pneumatici (€/pezzo/mese)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {['nuovo', 'usato'].map(tipo => (
            <div key={tipo} className="mb-4">
              <h4 className="font-medium text-sm text-slate-700 mb-2 uppercase">{tipo}</h4>
              <table className="w-full text-sm border-collapse max-w-lg">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border px-3 py-1.5 text-left">Durata</th>
                    <th className="border px-3 py-1.5 text-center">Invernali (€/pz)</th>
                    <th className="border px-3 py-1.5 text-center">Estive (€/pz)</th>
                  </tr>
                </thead>
                <tbody>
                  {DURATE.map(d => (
                    <tr key={d}>
                      <td className="border px-3 py-1 font-medium">{d} mesi</td>
                      <td className="border px-1 py-1">
                        <Input type="number" step="0.0001" min="0"
                          value={gommeCosti[tipo]?.[d]?.invernali || 0}
                          onChange={e => updateGomme(tipo, d, 'invernali', e.target.value)}
                          className="h-7 text-sm text-center" />
                      </td>
                      <td className="border px-1 py-1">
                        <Input type="number" step="0.0001" min="0"
                          value={gommeCosti[tipo]?.[d]?.estive || 0}
                          onChange={e => updateGomme(tipo, d, 'estive', e.target.value)}
                          className="h-7 text-sm text-center" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div className="flex justify-end">
            <Button size="sm" onClick={() => handleSave('gomme')} disabled={saving} className="gap-1">
              <Save className="w-3 h-3" /> Salva Costi Gomme
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto Sostitutiva */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Car className="w-4 h-4" /> Costi Auto Sostitutiva (€/mese)
          </CardTitle>
          <CardDescription>
            Disponibile per categorie B&gt;8, C&gt;8, D&gt;8 (oltre 8 ore lavorative, km illimitati). Costo fisso indipendente da durata/km, si somma al canone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm border-collapse max-w-2xl">
            <thead>
              <tr className="bg-slate-100">
                <th className="border px-3 py-1.5 text-left font-medium text-slate-700">Categoria</th>
                {AUTO_SOSTITUTIVA_TIPI.map(t => (
                  <th key={t.key} className="border px-3 py-1.5 text-center font-medium text-slate-700">{t.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {AUTO_SOSTITUTIVA_CATEGORIE.map(cat => (
                <tr key={cat}>
                  <td className="border px-3 py-1 font-medium text-slate-800">{cat}</td>
                  {AUTO_SOSTITUTIVA_TIPI.map(t => (
                    <td key={t.key} className="border px-1 py-1">
                      <Input type="number" step="0.01" min="0"
                        value={autoSostitutivaCosti?.[cat]?.[t.key] ?? 0}
                        onChange={e => updateAutoSostitutiva(cat, t.key, e.target.value)}
                        className="h-7 text-sm text-center"
                        data-testid={`auto-sost-${cat}-${t.key}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={() => handleSave('auto_sostitutiva')} disabled={saving} className="gap-1" data-testid="save-auto-sostitutiva">
              <Save className="w-3 h-3" /> Salva Auto Sostitutiva
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Fase 2 Task 2.3: Aliquota IVA anticipo (motore cliente) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Aliquota IVA Anticipo (Motore Cliente)</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Percentuale IVA applicata per scorporare l'anticipo cliente nel motore
            di calcolo dell'area riservata. Non incide sul catalogo agenti.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="w-40">
              <label className="text-xs text-slate-500">IVA %</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={ivaAnticipoPct}
                onChange={e => setIvaAnticipoPct(e.target.value)}
                data-testid="input-iva-anticipo-pct"
              />
            </div>
            <Button size="sm" onClick={() => handleSave('iva_anticipo')} disabled={saving} className="gap-1" data-testid="save-iva-anticipo">
              <Save className="w-3 h-3" /> Salva IVA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Promo Nuovo Cliente / Rinnovo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Promo Nuovo Cliente / Rinnovo</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Importi promozionali applicati come sconto sul canone (importo / durata). Attivabili nel configuratore
            per veicoli Nuovo e Usato/Longrun. Non disponibili sui listini 4Vantage.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
            <div>
              <Label className="text-xs text-slate-500">Importo Promo Veicolo Nuovo (€)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={promoNuovo}
                onChange={e => setPromoNuovo(e.target.value)}
                data-testid="input-promo-nuovo"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Importo Promo Veicolo Usato (€)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={promoUsato}
                onChange={e => setPromoUsato(e.target.value)}
                data-testid="input-promo-usato"
              />
            </div>
          </div>
          <div className="mt-4 max-w-xl">
            <Label className="text-xs text-slate-500">
              Dicitura visibile in PDF (es. "Promo Primavera 2026")
            </Label>
            <Input
              type="text"
              maxLength={120}
              placeholder="Promo Nuovo Cliente / Rinnovo"
              value={promoPdfLabel}
              onChange={e => setPromoPdfLabel(e.target.value)}
              data-testid="input-promo-pdf-label"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Compare come voce nel PDF quando la promo è attiva. Modifica libera senza toccare la logica di calcolo.
            </p>
          </div>
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={() => handleSave('promo')} disabled={saving} className="gap-1" data-testid="save-promo">
              <Save className="w-3 h-3" /> Salva Importi Promo
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParametriCalcoloPage;
